// Cobrança (etapa E10) no app: lê a assinatura no banco (minha_assinatura,
// só o titular) e pede à função da Cloudflare (/api/assinatura) para assinar,
// cancelar ou desfazer o cancelamento. As contas e as regras ficam no banco.
import { MESES_ATE_APAGAR } from '../config.js';
import { executar, supabase } from '../supabase.js';
import { diasEntre } from './datas.js';
import { data, dinheiro, plural } from './formato.js';
import { registrarErro } from './registro-de-erros.js';

export async function minhaAssinatura(empresa) {
  return executar(supabase.rpc('minha_assinatura', { p_empresa: empresa }));
}

// "SEM_CAMINHAO: ative a placa…" → "Ative a placa…"
export function textoDoErro(texto) {
  const regra = /^([A-Z][A-Z_]+):\s*(.+)$/s.exec(String(texto ?? ''));
  const frase = (regra ? regra[2] : String(texto ?? '')).trim();
  return frase ? frase.charAt(0).toUpperCase() + frase.slice(1) : '';
}

export async function pedirAoServidor(acao, dados, { buscar = (...a) => fetch(...a), sessaoAtual = () => supabase.auth.getSession() } = {}) {
  const { data: atual } = await sessaoAtual();
  const token = atual?.session?.access_token;
  if (!token) {
    const erro = new Error('SESSAO: entre de novo no app e tente outra vez.');
    erro.paraMostrar = 'Entre de novo no app e tente outra vez.';
    throw erro;
  }
  const resposta = await buscar('/api/assinatura', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ acao, ...dados }),
  });
  const corpo = await resposta.json().catch(() => ({}));
  if (resposta.ok && !corpo.erro) return corpo;
  const paraMostrar = textoDoErro(corpo.erro) || 'Não deu certo agora. Tente de novo em alguns minutos.';
  // Com o "CODIGO: frase", a tela mostra só a frase (mensagemDeErro).
  const erro = new Error(/^[A-Z][A-Z_]+:/.test(corpo.erro ?? '') ? corpo.erro : `COBRANCA: ${paraMostrar}`);
  erro.status = resposta.status;
  erro.paraMostrar = paraMostrar;
  if (resposta.status >= 500 && resposta.status !== 503) {
    registrarErro(new Error(`cobrança (${acao}): HTTP ${resposta.status} ${corpo.erro ?? ''}`.trim()), { onde: 'cobranca', mensagem: erro.paraMostrar });
  }
  throw erro;
}

export const SITUACAO_DA_FATURA = {
  a_enviar: ['Sendo gerada', 'info'],
  aberta: ['A pagar', 'alerta'],
  vencida: ['Vencida', 'perigo'],
  paga: ['Paga', 'ok'],
  cancelada: ['Cancelada', ''],
  estornada: ['Estornada', 'perigo'],
};

// "HSA Frota: assinatura mensal de 01/11/2026 a 30/11/2026 (2 caminhões)" → sem o "HSA Frota: "
export function tituloDaFatura(f) {
  const texto = String(f.descricao ?? '').replace(/^HSA Frota:\s*/, '');
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

// Até quando a assinatura atual está paga (o fim do último período pago).
export function pagoAte(info) {
  const a = info.assinatura;
  if (!a) return null;
  const fins = (info.faturas ?? [])
    .filter((f) => f.tipo === 'ciclo' && f.situacao === 'paga' && f.periodo_inicio >= a.inicio)
    .map((f) => f.periodo_fim);
  return fins.length ? fins.sort().at(-1) : null;
}

// Se cancelar hoje, até quando a conta continua gravando.
export function terminaSeCancelar(info) {
  const ate = pagoAte(info);
  return ate && ate >= info.hoje ? ate : info.hoje;
}

// O que a tela mostra no alto: título, texto e o tom da faixa.
export function resumoDaAssinatura(info) {
  const { hoje, empresa: e, assinatura: a } = info;
  const ativa = a && ['aguardando', 'em_dia', 'atrasada', 'cancelada'].includes(a.situacao);
  if (!ativa) {
    if (e.situacao === 'ativa') return { tom: 'ok', titulo: 'Conta sem cobrança', texto: 'Esta conta não tem cobrança.', assinar: false };
    if (e.situacao === 'cancelada') {
      return {
        tom: 'perigo',
        titulo: 'Assinatura encerrada',
        texto: e.apagar_em
          ? `A conta está só para consulta. Os dados serão apagados em ${data(e.apagar_em)} (avisamos antes). Para voltar a lançar, assine de novo.`
          : 'A conta está só para consulta. Para voltar a lançar, assine de novo.',
        assinar: true,
      };
    }
    if (e.situacao === 'teste') {
      const dias = diasEntre(hoje, e.teste_ate);
      if (dias >= 0) {
        return {
          tom: 'info',
          titulo: dias === 0 ? 'Hoje é o último dia do teste grátis' : `Teste grátis até ${data(e.teste_ate)}`,
          texto: 'Assine quando quiser: o teste continua até o fim, e a primeira fatura vence no dia seguinte.',
          assinar: true,
        };
      }
      return {
        tom: 'perigo',
        titulo: 'O teste grátis terminou',
        texto: `Os dados continuam aqui, só para consulta. Assine para voltar a lançar. Sem assinatura, eles são apagados ${MESES_ATE_APAGAR} meses depois do fim do teste (avisamos antes).`,
        assinar: true,
      };
    }
    return { tom: 'perigo', titulo: 'Conta só para consulta', texto: 'Assine para voltar a lançar.', assinar: true };
  }

  const plano = a.ciclo === 'anual' ? `Plano anual: renova todo ano em ${data(a.inicio).slice(0, 5)}.` : `Plano mensal: vence todo dia ${a.dia}.`;
  const lancamento = a.lancamento && a.lancamento_ate > hoje ? ` Preço de lançamento até ${data(a.lancamento_ate)}.` : '';
  if (a.situacao === 'cancelada') {
    return {
      tom: 'alerta',
      titulo: 'Assinatura cancelada',
      texto: `A conta continua funcionando até ${data(a.termina_em)}. Depois, fica só para consulta, e os dados são apagados ${MESES_ATE_APAGAR} meses depois (avisamos antes).`,
      desfazer: a.termina_em >= hoje,
    };
  }
  const gravavel = e.carencia_ate ? e.carencia_ate >= hoje : e.situacao === 'ativa' || (e.situacao === 'teste' && e.teste_ate >= hoje);
  if (a.situacao === 'aguardando') {
    const primeira = (info.faturas ?? []).filter((f) => f.tipo === 'ciclo' && f.periodo_inicio === a.inicio).at(0);
    const vence = primeira ? ` A primeira fatura vence em ${data(primeira.vencimento)}.` : '';
    if (!gravavel) {
      return { tom: 'perigo', titulo: 'Fatura em aberto', texto: `A conta está só para consulta até o pagamento.${vence}`, cancelar: true };
    }
    const teste = e.situacao === 'teste' && e.teste_ate >= hoje ? ` O teste grátis continua até ${data(e.teste_ate)}.` : '';
    const prazo = e.carencia_ate && diasEntre(hoje, e.carencia_ate) < 10 ? ` Pague até ${data(e.carencia_ate)} para a conta não ficar só para consulta.` : '';
    return { tom: 'info', titulo: 'Esperando o pagamento da primeira fatura', texto: `${plano}${vence}${teste}${prazo}${lancamento}`.trim(), cancelar: true };
  }
  if (a.situacao === 'atrasada' || !gravavel) {
    return { tom: 'perigo', titulo: 'Fatura atrasada', texto: 'A conta está só para consulta até o pagamento. Depois de pagar, ela volta a gravar em alguns minutos.', cancelar: true };
  }
  const prazo = e.carencia_ate && diasEntre(hoje, e.carencia_ate) < 10
    ? ` Há uma fatura vencida: pague até ${data(e.carencia_ate)} para a conta não ficar só para consulta.` : '';
  return { tom: prazo ? 'alerta' : 'ok', titulo: 'Assinatura em dia', texto: `${plano}${lancamento}${prazo}`.trim(), cancelar: true };
}

// As opções de assinatura com o preço de hoje.
export function opcoesDeAssinatura(info) {
  const n = info.caminhoes;
  const { mensal, anual } = info.previa;
  return [
    ['mensal', 'Mensal', `${dinheiro(mensal)} por mês`],
    ['anual', 'Anual', `${dinheiro(anual)} por ano (paga 11 meses)`],
  ].map(([valor, rotulo, preco]) => ({ valor, rotulo, preco, caminhoes: n }));
}

export function textoDosCaminhoes(n) {
  return n === 0
    ? 'Nenhum caminhão com placa ativa.'
    : `${plural(n, 'caminhão com placa ativa', 'caminhões com placa ativa')}. O valor muda quando você ativa ou retira placas.`;
}
